import { drawing as draw, geometry as geom } from '@progress/kendo-drawing';
import { deepExtend } from './utils/deep-extend';
import { Observable } from './utils/Observable';
import { Shape, ShapesQuadTree, Connection, Connector, mwDelta, DiagramElement, indicesOfItems, InactiveItemsCollection } from './dom';
import { Group, Canvas, CompositeTransform } from './svg';
import { Point, randomId, Rect, RectAlign, SpringLayout } from './math';
import { ResizingAdorner, ConnectorsAdorner, Selector, ToolService, AddConnectionUnit, AddShapeUnit, ToFrontUnit, ToBackUnit, TransformUnit, LayoutUndoUnit, UndoRedoService, DeleteConnectionUnit, DeleteShapeUnit } from './services';
import { copyDefaultOptions, defaultOptions, elementHeight, elementWidth, events, elementOffset, outerHeight, splitDiagramElements, domEvents } from './diagram-utils';
import { mobileOS } from '@progress/kendo-common';
import { defined, first, insert, isDefined, isString, isUndefined, remove, noop, contains } from './utils';
import { CHANGE, DEFAULT_CANVAS_HEIGHT, DEFAULT_CANVAS_WIDTH, MAX_VALUE, MIN_VALUE, MOBILE_PAN_DISTANCE, MOBILE_ZOOM_RATE, MOUSE_ENTER, MOUSE_LEAVE, PAN, ROTATED, SELECT, TOOLTIP_HIDE, TOOLTIP_SHOW, TRANSFORMED, ZOOM_END, ZOOM_START } from './constants';
import UserEvents from './utils/user-events';
import { LayeredLayout, LayoutDefaultOptions, LayoutState, TreeLayout } from './layout';
import { macOS } from './utils/macOS';
import { Navigation } from './navigation';
export class Diagram extends Observable {
    /**
     * Creates a new Diagram instance.
     * @param element The HTML element that will contain the diagram
     * @param userOptions Configuration options for the diagram
     * @param themeOptions Theme-specific options
     */
    constructor(element, userOptions, themeOptions) {
        super();
        /** @hidden */
        this._clipboard = [];
        /** @hidden */
        this._connectionsDataMap = {};
        /** @hidden */
        this._dataMap = {};
        /** @hidden */
        this._inactiveShapeItems = new InactiveItemsCollection();
        /** @hidden */
        this._selectedItems = [];
        /** Array of all shapes in the diagram. */
        this.shapes = [];
        /** Array of all connections in the diagram. */
        this.connections = [];
        /** @hidden */
        this._deferredConnectionUpdates = [];
        /** @hidden */
        this._domEvent = (nativeEvent) => {
            const meta = this._meta(nativeEvent);
            const point = this._eventPositions(nativeEvent);
            const item = this.toolService._hitTest(point);
            this.trigger(nativeEvent.type, { nativeEvent, item, point, meta });
        };
        this.element = element;
        this.options = deepExtend({ createToolBar: noop, destroyToolBar: noop }, defaultOptions, userOptions);
        this.events = events;
        this._initTheme(themeOptions);
        this._initElements();
        this._extendLayoutOptions(this.options);
        this._initDefaults(userOptions);
        this._interactionDefaults();
        this._initCanvas();
        this.mainLayer = new Group({
            id: 'main-layer'
        });
        this.canvas.append(this.mainLayer);
        this._shapesQuadTree = new ShapesQuadTree(this);
        this._pan = new Point();
        this._adorners = [];
        this.adornerLayer = new Group({
            id: 'adorner-layer'
        });
        this.canvas.append(this.adornerLayer);
        this._createHandlers();
        this._initialize();
        this._resizingAdorner = new ResizingAdorner(this, { editable: this.options.editable });
        this._connectorsAdorner = new ConnectorsAdorner(this);
        this._adorn(this._resizingAdorner, true);
        this._adorn(this._connectorsAdorner, true);
        this.selector = new Selector(this);
        // TODO: We may consider using real Clipboard API once is supported by the standard.
        this._clipboard.length = 0;
        this.pauseMouseHandlers = false;
        this._navigation = new Navigation(this);
    }
    /** @hidden */
    _createShape(dataItem, options) {
        options = deepExtend({}, this.options.shapeDefaults, options);
        options.dataItem = dataItem;
        const shape = new Shape(options, this);
        return shape;
    }
    /** @hidden */
    _createConnection(dataItem, source, target) {
        const options = deepExtend({}, this.options.connectionDefaults);
        options.dataItem = dataItem;
        const connection = new Connection(source || new Point(), target || new Point(), options);
        return connection;
    }
    /** @hidden */
    _initElements() {
        this.element.innerHTML = '';
        this.element.style.position = 'relative';
        this.element.classList.add('k-diagram');
        this.scrollable = document.createElement('div');
        this.element.appendChild(this.scrollable);
        const accessibility = this.options.accessibility || {};
        this._setAttributes('role', accessibility.role);
        this._setAttributes('aria-label', accessibility.ariaLabel);
        this._setAttributes('aria-roledescription', accessibility.ariaRoleDescription);
        this.wrapper = this.element;
    }
    /** @hidden */
    _setAttributes(attrName, value) {
        if (defined(value)) {
            this.element.setAttribute(attrName, String(value));
        }
    }
    /** @hidden */
    _initDefaults(userOptions) {
        const options = this.options;
        const editable = options.editable;
        const shapeDefaults = options.shapeDefaults;
        const connectionDefaults = options.connectionDefaults;
        const userShapeDefaults = (userOptions || {}).shapeDefaults;
        if (editable === false) {
            shapeDefaults.editable = false;
            connectionDefaults.editable = false;
        }
        else {
            copyDefaultOptions(editable, shapeDefaults.editable, ['drag', 'remove', 'connect']);
            copyDefaultOptions(editable, connectionDefaults.editable, ['drag', 'remove']);
        }
        if (userShapeDefaults && userShapeDefaults.connectors) {
            options.shapeDefaults.connectors = userShapeDefaults.connectors;
        }
    }
    /** @hidden */
    _interactionDefaults() {
        const options = this.options;
        const selectable = options.selectable;
        const pannable = options.pannable;
        const mobile = this._mobileOS();
        if (selectable && !defined(selectable.multiple)) {
            options.selectable = deepExtend({
                multiple: mobile ? false : true
            }, options.selectable);
        }
        if (pannable && !defined(pannable.key)) {
            options.pannable = deepExtend({
                key: mobile ? 'none' : 'ctrl'
            }, options.pannable);
        }
    }
    /** @hidden */
    _initCanvas() {
        const canvasContainer = document.createElement('div');
        canvasContainer.classList.add('k-layer');
        this.scrollable.appendChild(canvasContainer);
        const viewPort = this.viewport();
        this.canvas = new (this.options.Canvas || Canvas)(canvasContainer, {
            width: viewPort.width || DEFAULT_CANVAS_WIDTH,
            height: viewPort.height || DEFAULT_CANVAS_HEIGHT
        });
    }
    /** @hidden */
    _createHandlers() {
        const element = this.element;
        this._wheelHandler = this._wheelHandler || this._wheel.bind(this);
        this._keydownHandler = this._keydownHandler || this._keydown.bind(this);
        if (this._mobileOS() && this._mobileOS().browser.mobilesafari) {
            element.addEventListener('mousewheel', this._wheelHandler);
        }
        else {
            element.addEventListener('wheel', this._wheelHandler);
        }
        element.addEventListener('keydown', this._keydownHandler);
        this._userEvents = new UserEvents(this.scrollable, {
            multiTouch: true,
            fastTap: true,
            tap: this._tap.bind(this),
            start: this._dragStart.bind(this),
            move: this._drag.bind(this),
            end: this._dragEnd.bind(this),
            gesturestart: this._gestureStart.bind(this),
            gesturechange: this._gestureChange.bind(this),
            gestureend: this._gestureEnd.bind(this),
            doubleTap: this._doubleTap.bind(this),
            supportDoubleTap: true
        });
        this.toolService = new ToolService(this);
        this._mouseoverHandler = this._mouseoverHandler || this._mouseover.bind(this);
        this._mouseoutHandler = this._mouseoutHandler || this._mouseout.bind(this);
        this._mouseMoveHandler = this._mouseMoveHandler || this._mouseMove.bind(this);
        this._mouseDownHandler = this._mouseDownHandler || this._mouseDown.bind(this);
        this._mouseUpHandler = this._mouseUpHandler || this._mouseUp.bind(this);
        this.scrollable.addEventListener('mouseover', this._mouseoverHandler);
        this.scrollable.addEventListener('mouseout', this._mouseoutHandler);
        this.scrollable.addEventListener('mousemove', this._mouseMoveHandler);
        this.scrollable.addEventListener('mousedown', this._mouseDownHandler);
        this.scrollable.addEventListener('mouseup', this._mouseUpHandler);
        domEvents.forEach(event => {
            this.scrollable.addEventListener(event, this._domEvent);
        });
        this._initResizeObserver();
        this.bind(ZOOM_START, this._destroyToolBar.bind(this));
        this.bind(PAN, this._destroyToolBar.bind(this));
        this.bind(MOUSE_ENTER, this._onMouseEnter.bind(this));
        this.bind(MOUSE_LEAVE, this._onMouseLeave.bind(this));
    }
    _onMouseEnter(event) {
        const { delay } = this.options.tooltip;
        this._tooltipTimeOut = setTimeout(() => {
            this.trigger(TOOLTIP_SHOW, event);
            this._tooltipTimeOut = null;
        }, delay);
    }
    _onMouseLeave(event) {
        clearTimeout(this._tooltipTimeOut);
        this.trigger(TOOLTIP_HIDE, event);
    }
    /** @hidden */
    _initResizeObserver() {
        const observer = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                const { width, height } = entry.contentRect;
                if (entry.target !== this.element ||
                    (this.size && this.size.width === width && this.size.height === height)) {
                    return;
                }
                this.size = { width, height };
                this._resize();
                this.trigger('resize', this.size);
            });
        });
        this._resizeObserver = observer;
        observer.observe(this.element);
    }
    /** @hidden */
    _destroyResizeObserver() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }
    /** @hidden */
    _dragStart(e) {
        this._pauseMouseHandlers = true;
        const point = this._eventPositions(e, true);
        if (this.toolService.start(point, this._meta(e), e.event)) {
            this._destroyToolBar();
            e.preventDefault();
        }
    }
    /** @hidden */
    _drag(e) {
        const p = this._eventPositions(e);
        if (this.toolService.move(p, this._meta(e), e.event)) {
            e.preventDefault();
        }
    }
    /** @hidden */
    _dragEnd(e) {
        this._pauseMouseHandlers = false;
        const p = this._eventPositions(e);
        if (this.toolService.end(p, this._meta(e), e.event)) {
            this.options.createToolBar();
            e.preventDefault();
        }
    }
    /** @hidden */
    _mouseMove(e) {
        if (!this._pauseMouseHandlers) {
            const p = this._eventPositions(e);
            this.toolService._updateHoveredItem(p, this._meta(e), e);
            this.toolService._updateCursor(p);
        }
    }
    /** @hidden */
    _mouseDown() {
        this._pauseMouseHandlers = true;
    }
    /** @hidden */
    _mouseUp() {
        this._pauseMouseHandlers = false;
    }
    /** @hidden */
    _tap(e) {
        const toolService = this.toolService;
        const selectable = this.options.selectable;
        const point = this._eventPositions(e);
        const focused = this.focus();
        const meta = this._meta(e);
        toolService._updateHoveredItem(point, meta, e.event);
        if (toolService.hoveredItem) {
            const item = toolService.hoveredItem;
            this.trigger('click', {
                nativeEvent: e.event,
                item: item,
                point: point,
                meta: meta
            });
            if (selectable && item.options.selectable !== false) {
                const multiple = selectable.multiple !== false;
                const ctrlPressed = mobileOS || meta.ctrlKey || (meta.metaKey && macOS());
                if (item.isSelected) {
                    if (ctrlPressed) {
                        this._destroyToolBar();
                        item.select(false);
                    }
                    else {
                        this.options.createToolBar(focused);
                    }
                }
                else {
                    this._destroyToolBar();
                    this.select(item, {
                        addToSelection: multiple && ctrlPressed
                    });
                    this.options.createToolBar(focused);
                }
            }
        }
        else if (selectable) {
            this._destroyToolBar();
            this.deselect();
        }
    }
    /** @hidden */
    _keydown(e) {
        if (this.toolService.keyDown(e.keyCode, this._meta(e))) {
            e.preventDefault();
        }
    }
    /** @hidden */
    _wheel(e) {
        const delta = mwDelta(e), p = this._eventPositions(e), meta = deepExtend(this._meta(e), { delta: delta });
        if (this.toolService.wheel(p, meta, e)) {
            e.preventDefault();
        }
    }
    /** @hidden */
    _meta(e) {
        e = e.event || e;
        return { ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, shiftKey: e.shiftKey, type: e.type };
    }
    /** @hidden */
    _eventPositions(e, start) {
        let point;
        if (e.touch) {
            const field = start ? 'startLocation' : 'location';
            point = new Point(e.x[field], e.y[field]);
        }
        else {
            point = new Point(e.pageX, e.pageY);
        }
        return this.documentToModel(point);
    }
    /** @hidden */
    _gestureStart(e) {
        this._destroyToolBar();
        this.scroller.disable();
        const initialCenter = this.documentToModel(new Point(e.center.x, e.center.y));
        const eventArgs = {
            point: initialCenter,
            zoom: this.zoom()
        };
        if (this.trigger(ZOOM_START, eventArgs)) {
            return;
        }
        this._gesture = e;
        this._initialCenter = initialCenter;
    }
    /** @hidden */
    _gestureChange(e) {
        const previousGesture = this._gesture;
        const initialCenter = this._initialCenter;
        const center = this.documentToView(new Point(e.center.x, e.center.y));
        const scaleDelta = e.distance / previousGesture.distance;
        let zoom = this._zoom;
        let updateZoom = false;
        if (Math.abs(scaleDelta - 1) >= MOBILE_ZOOM_RATE) {
            this._zoom = zoom = this._getValidZoom(zoom * scaleDelta);
            this.options.zoom = zoom;
            this._gesture = e;
            updateZoom = true;
        }
        const zoomedPoint = initialCenter.times(zoom);
        const pan = center.minus(zoomedPoint);
        if (updateZoom || this._pan.distanceTo(pan) >= MOBILE_PAN_DISTANCE) {
            this._panTransform(pan);
            this._updateAdorners();
        }
        e.preventDefault();
    }
    /** @hidden */
    _doubleTap(e) {
        const mobile = this._mobileOS();
        if (!mobile) {
            return;
        }
        const pointPosition = this._eventPositions(e);
        const options = this.options;
        const zoomRate = options.zoomRate;
        let zoom = this.zoom() + zoomRate;
        const meta = this._meta(e);
        const zoomOptions = { point: pointPosition, meta: meta, zoom: zoom };
        if (this.trigger(ZOOM_START, zoomOptions)) {
            return;
        }
        zoom = draw.util.round(Math.max(options.zoomMin, Math.min(options.zoomMax, zoom)), 2);
        zoomOptions.zoom = zoom;
        this.zoom(zoom, zoomOptions);
        this.trigger(ZOOM_END, zoomOptions);
    }
    /** @hidden */
    _gestureEnd() {
        if (this.options.pannable !== false) {
            this.scroller.enable();
        }
        this.trigger(ZOOM_END, {
            point: this._initialCenter,
            zoom: this.zoom()
        });
    }
    /** @hidden */
    _resize() {
        const viewport = this.viewport();
        if (this.canvas) {
            this.canvas.size(viewport);
        }
        if (this.scrollable && this.toolBar) {
            this.scrollable.style.height = viewport.height + 'px';
        }
    }
    /** @hidden */
    _mouseover(e) {
        const node = e.target._kendoNode;
        if (node && node.srcElement._hover) {
            node.srcElement._hover(true, node.srcElement);
        }
    }
    /** @hidden */
    _mouseout(e) {
        const node = e.target._kendoNode;
        if (node && node.srcElement._hover) {
            node.srcElement._hover(false, node.srcElement);
        }
    }
    /** @hidden */
    _initTheme(themeOptions) {
        var _a, _b, _c;
        this.options = deepExtend({}, themeOptions, this.options);
        if (this.options.editable === true) {
            this.options.editable = (themeOptions || {}).editable;
        }
        else if (this.options.editable && this.options.editable.resize === true) {
            // If user passed resize: true, use theme's resize options (they will be merged with defaults in ResizingAdorner)
            this.options.editable.resize = (themeOptions.editable || {}).resize;
        }
        if (((_a = this.options.connectionDefaults) === null || _a === void 0 ? void 0 : _a.editable) === true) {
            this.options.connectionDefaults.editable = themeOptions.connectionDefaults.editable;
        }
        else if (((_c = (_b = this.options.connectionDefaults) === null || _b === void 0 ? void 0 : _b.editable) === null || _c === void 0 ? void 0 : _c.points) === true) {
            this.options.connectionDefaults.editable.points = themeOptions.connectionDefaults.editable.points;
        }
    }
    /** @hidden */
    _createOptionElements() {
        const options = this.options;
        const shapesLength = options.shapes.length;
        if (shapesLength) {
            this._createShapes();
        }
        if (options.connections.length) {
            this._createConnections();
        }
        if (shapesLength && options.layout) {
            this.layout(options.layout);
        }
        this.undoRedoService.clear();
    }
    /** @hidden */
    _createShapes() {
        const options = this.options, shapes = options.shapes;
        let shape, i;
        for (i = 0; i < shapes.length; i++) {
            shape = shapes[i];
            this.addShape(shape);
        }
    }
    /** @hidden */
    _createConnections() {
        const options = this.options, defaults = options.connectionDefaults, connections = options.connections;
        let conn, source, target, i;
        for (i = 0; i < connections.length; i++) {
            conn = connections[i];
            source = this._findConnectionTarget(conn.from);
            target = this._findConnectionTarget(conn.to);
            if (this.options.connect) {
                this.options.connect(source, target, deepExtend({}, defaults, conn));
            }
            else {
                this.connect(source, target, deepExtend({}, defaults, conn));
            }
        }
    }
    /** @hidden */
    _findConnectionTarget(options) {
        options = options || {};
        const shapeId = isString(options) ? options : options.shapeId || options.id;
        let target;
        if (shapeId) {
            target = this.getShapeById(shapeId);
            if (options.connector) {
                target = target.getConnector(options.connector);
            }
        }
        else {
            target = new Point(options.x || 0, options.y || 0);
        }
        return target;
    }
    /**
     * Destroys the diagram and cleans up all resources.
     * Removes event listeners, destroys components, and clears all elements.
     */
    destroy() {
        var _a;
        super.destroy();
        this._destroyResizeObserver();
        (_a = this._navigation) === null || _a === void 0 ? void 0 : _a.destroy();
        this._navigation = null;
        if (this._userEvents) {
            this._userEvents.destroy();
        }
        this.clear();
        this.element.removeEventListener('mousewheel', this._wheelHandler);
        this.element.removeEventListener('wheel', this._wheelHandler);
        this.element.removeEventListener('keydown', this._keydownHandler);
        this.scrollable.removeEventListener('mouseover', this._mouseoverHandler);
        this.scrollable.removeEventListener('mouseout', this._mouseoutHandler);
        this.scrollable.removeEventListener('mousemove', this._mouseMoveHandler);
        this.scrollable.removeEventListener('mousedown', this._mouseDownHandler);
        this.scrollable.removeEventListener('mouseup', this._mouseUpHandler);
        domEvents.forEach(event => {
            this.scrollable.removeEventListener(event, this._domEvent);
        });
        this.canvas.destroy(true);
        this.canvas = undefined;
        this.destroyScroller();
        this._destroyGlobalToolBar();
        this._destroyToolBar();
        this._inactiveShapeItems.destroy();
    }
    /**
     * Destroys the scroller component and removes its element.
     */
    destroyScroller() {
        const scroller = this.scroller;
        if (!scroller) {
            return;
        }
        scroller.destroy();
        scroller.element.remove();
        this.scroller = null;
    }
    /**
     * Serializes the diagram to a JSON object containing shapes and connections.
     * @returns Object with shapes and connections arrays
     */
    save() {
        const json = {
            shapes: [],
            connections: []
        };
        let i, connection, shape;
        for (i = 0; i < this.shapes.length; i++) {
            shape = this.shapes[i];
            if (shape.options.serializable) {
                const shapeOptions = deepExtend({}, shape.options);
                // Restore original dimensions if they were explicitly provided to prevent size inflation
                if (shape._originalWidth !== undefined) {
                    shapeOptions.width = shape._originalWidth;
                }
                if (shape._originalHeight !== undefined) {
                    shapeOptions.height = shape._originalHeight;
                }
                json.shapes.push(shapeOptions);
            }
        }
        for (i = 0; i < this.connections.length; i++) {
            connection = this.connections[i];
            json.connections.push(deepExtend({}, connection.options, connection.toJSON()));
        }
        return json;
    }
    /**
     * Sets focus to the diagram element.
     * @returns True if focus was set, undefined otherwise
     */
    focus() {
        if (this.element !== this.element.ownerDocument.activeElement) {
            const element = this.element, containers = [], offsets = [], documentElement = element.ownerDocument.documentElement;
            let scrollContainer = element, i;
            do {
                scrollContainer = scrollContainer.parentNode;
                if (scrollContainer.scrollHeight > scrollContainer.clientHeight) {
                    containers.push(scrollContainer);
                    offsets.push(scrollContainer.scrollTop);
                }
            } while (scrollContainer !== documentElement);
            element.focus({ preventScroll: true });
            for (i = 0; i < containers.length; i++) {
                containers[i].scrollTop = offsets[i];
            }
            return true;
        }
    }
    /**
     * Loads diagram data and recreates shapes and connections.
     * @param options The diagram options containing shapes and connections data
     */
    load(options) {
        this.clear();
        this.setOptions(options);
        this._createShapes();
        this._createConnections();
        this.undoRedoService.clear();
    }
    /**
     * Sets options for the diagram by deep extending the current options.
     * @param options The options to merge with current diagram options
     */
    setOptions(options) {
        deepExtend(this.options, options);
    }
    /**
     * Clears the diagram by removing all selections and elements, then reinitializes.
     */
    clear() {
        this.select(false);
        this.mainLayer.clear();
        this._shapesQuadTree.clear();
        this._initialize();
    }
    /**
     * Determines whether the the two items are connected.
     *
     * @param source Shape, Connector, Point.
     * @param target Shape, Connector, Point.
     * @returns true if the two items are connected.
     */
    connected(source, target) {
        for (let i = 0; i < this.connections.length; i++) {
            const c = this.connections[i];
            if (c.from === source && c.to === target) {
                return true;
            }
        }
        return false;
    }
    /**
     * Adds connection to the diagram.
     *
     * @param connection Connection.
     * @param undoable Boolean.
     * @returns The newly created connection.
     */
    addConnection(connection, undoable) {
        if (undoable !== false) {
            this.undoRedoService.add(new AddConnectionUnit(connection, this), false);
        }
        connection.diagram = this;
        connection._setOptionsFromModel();
        connection.refresh();
        this.mainLayer.append(connection.visual);
        this.connections.push(connection);
        this.trigger(CHANGE, {
            added: [connection],
            removed: []
        });
        return connection;
    }
    /**
     * Adds shape to the diagram.
     *
     * @param item Shape, Point. If point is passed it will be created new Shape and positioned at that point.
     * @param options. The options to be passed to the newly created Shape.
     * @returns The newly created shape.
     */
    addShape(item, undoable) {
        let shape, shapeDefaults = this.options.shapeDefaults;
        if (item instanceof Shape) {
            shape = item;
            this._parseBounds(shape.bounds());
        }
        else if (!(item.prototype)) {
            shapeDefaults = deepExtend({}, shapeDefaults, item || {});
            shape = new Shape(shapeDefaults, this);
            this._parseBounds(shape.bounds());
        }
        else {
            return;
        }
        if (undoable !== false) {
            this.undoRedoService.add(new AddShapeUnit(shape, this), false);
        }
        this.shapes.push(shape);
        if (shape.diagram !== this) {
            this._shapesQuadTree.insert(shape);
            shape.diagram = this;
        }
        this.mainLayer.append(shape.visual);
        this.trigger(CHANGE, {
            added: [shape],
            removed: []
        });
        return shape;
    }
    /**
     * Removes items (or single item) from the diagram.
     *
     * @param items DiagramElement, Array of Items.
     * @param undoable.
     */
    remove(items, undoable) {
        items = Array.isArray(items) ? items.slice(0) : [items];
        const elements = splitDiagramElements(items);
        const shapes = elements.shapes;
        const connections = elements.connections;
        let i;
        if (!defined(undoable)) {
            undoable = true;
        }
        if (undoable) {
            this.undoRedoService.begin();
        }
        this._suspendModelRefresh();
        for (i = shapes.length - 1; i >= 0; i--) {
            this._removeItem(shapes[i], undoable, connections);
        }
        for (i = connections.length - 1; i >= 0; i--) {
            this._removeItem(connections[i], undoable);
        }
        this._resumeModelRefresh();
        if (undoable) {
            this.undoRedoService.commit(false);
        }
        this.trigger(CHANGE, {
            added: [],
            removed: items
        });
    }
    /** @hidden */
    _addConnection(connection, undoable) {
        if (this.options._addConnection) {
            return this.options._addConnection(connection, undoable);
        }
        else if (!this.trigger('add', { connection: connection })) {
            this.addConnection(connection, undoable);
            connection._updateConnectors();
            return connection;
        }
    }
    /** @hidden */
    _addShape(shape, undoable) {
        if (this.options._addShape) {
            return this.options._addShape(shape, undoable);
        }
        else if (!this.trigger('add', { shape: shape })) {
            return this.addShape(shape, undoable);
        }
    }
    /** @hidden */
    _parseBounds(bounds) {
        bounds.x = typeof (bounds.x) == 'string' ? parseFloat(bounds.x) : bounds.x;
        bounds.y = typeof (bounds.y) == 'string' ? parseFloat(bounds.y) : bounds.y;
    }
    /** @hidden */
    _shouldRefresh() {
        return !this._suspended;
    }
    /** @hidden */
    _suspendModelRefresh() {
        this._suspended = (this._suspended || 0) + 1;
    }
    /** @hidden */
    _resumeModelRefresh() {
        this._suspended = Math.max((this._suspended || 0) - 1, 0);
    }
    /** @hidden */
    _triggerRemove(items) {
        const toRemove = [];
        let item, args, editable;
        for (let idx = 0; idx < items.length; idx++) {
            item = items[idx];
            editable = item.options.editable;
            if (item instanceof Shape) {
                args = { shape: item };
            }
            else {
                args = { connection: item };
            }
            if (editable && editable.remove !== false && !this.trigger('remove', args)) {
                toRemove.push(item);
            }
        }
        return toRemove;
    }
    /** @hidden */
    _addConnections(connections, undoable) {
        const length = connections.length;
        for (let i = 0; i < length; i++) {
            const dataItem = connections[i];
            this._addConnectionDataItem(dataItem, undoable);
        }
    }
    /** @hidden */
    _addConnectionDataItem(dataItem, undoable) {
        if (!this._connectionsDataMap[dataItem.uid]) {
            let from = this._validateConnector(dataItem.from);
            if (!defined(from) || from === null) {
                from = new Point(dataItem.fromX, dataItem.fromY);
            }
            let to = this._validateConnector(dataItem.to);
            if (!defined(to) || to === null) {
                to = new Point(dataItem.toX, dataItem.toY);
            }
            if (defined(from) && defined(to)) {
                const options = deepExtend({}, this.options.connectionDefaults);
                options.dataItem = dataItem;
                const connection = new Connection(from, to, options);
                this._connectionsDataMap[dataItem.uid] = connection;
                this.addConnection(connection, undoable);
            }
        }
    }
    /** @hidden */
    _validateConnector(value) {
        let connector;
        if (defined(value) && value !== null) {
            connector = this._dataMap[value];
        }
        return connector;
    }
    /** @hidden */
    _addDataItems(items, parent) {
        let item, idx, shape, parentShape;
        for (idx = 0; idx < items.length; idx++) {
            item = items[idx];
            shape = this._addDataItemByUid(item);
            parentShape = this._addDataItemByUid(parent);
            if (parentShape && !this.connected(parentShape, shape)) { // check if connected to not duplicate connections.
                this.connect(parentShape, shape);
            }
        }
    }
    /**
     * Creates a connection between two endpoints (shapes, connectors, or points).
     * @param source The source endpoint
     * @param target The target endpoint
     * @param options Optional connection configuration
     * @returns The newly created connection
     */
    connect(source, target, options) {
        const resolvedOptions = deepExtend({}, this.options.connectionDefaults, options);
        const connection = new Connection(source, target, resolvedOptions);
        return this.addConnection(connection);
    }
    /**
     * Executes the next undoable action on top of the undo stack if any.
     */
    undo() {
        this.undoRedoService.undo();
    }
    /**
     * Executes the previous undoable action on top of the redo stack if any.
     */
    redo() {
        this.undoRedoService.redo();
    }
    /**
     * Selects items on the basis of the given input or returns the current selection if none.
     *
     * @param itemsOrRect DiagramElement, Array of elements, "All", false or Rect. A value 'false' will deselect everything.
     * @param options
     * @returns {Array} The currently selected items.
     */
    select(item, options) {
        if (isDefined(item)) {
            options = deepExtend({ addToSelection: false }, options);
            const addToSelection = options.addToSelection, selected = [];
            let items = [], i, element;
            if (!addToSelection) {
                this.deselect();
            }
            this._internalSelection = true;
            if (item instanceof Array) {
                items = item;
            }
            else if (item instanceof DiagramElement) {
                items = [item];
            }
            for (i = 0; i < items.length; i++) {
                element = items[i];
                if (element.select(true)) {
                    selected.push(element);
                }
            }
            this._selectionChanged(selected, []);
            this._internalSelection = false;
        }
        else {
            return this._selectedItems;
        }
    }
    /**
     * Selects all shapes and connections in the diagram.
     */
    selectAll() {
        this.select(this.shapes.concat(this.connections));
    }
    /**
     * Selects shapes and connections within a rectangular area.
     * @param rect The rectangular area to select items within
     */
    selectArea(rect) {
        let i, items, item;
        this._internalSelection = true;
        const selected = [];
        if (rect instanceof Rect) {
            items = this.shapes.concat(this.connections);
            for (i = 0; i < items.length; i++) {
                item = items[i];
                if ((!rect || item._hitTest(rect)) && item.options.enable) {
                    if (item.select(true)) {
                        selected.push(item);
                    }
                }
            }
        }
        this._selectionChanged(selected, []);
        this._internalSelection = false;
    }
    /**
     * Deselects items from the current selection.
     * @param item The item(s) to deselect. If not provided, deselects all items.
     */
    deselect(item) {
        this._internalSelection = true;
        const deselected = [];
        let items = [], element, i;
        if (item instanceof Array) {
            items = item;
        }
        else if (item instanceof DiagramElement) {
            items.push(item);
        }
        else if (!isDefined(item)) {
            items = this._selectedItems.slice(0);
        }
        for (i = 0; i < items.length; i++) {
            element = items[i];
            if (element.select(false)) {
                deselected.push(element);
            }
        }
        this._selectionChanged([], deselected);
        this._internalSelection = false;
    }
    /**
     * Brings to front the passed items.
     *
     * @param items DiagramElement, Array of Items.
     * @param undoable. By default the action is undoable.
     */
    toFront(items, undoable) {
        if (!items) {
            items = this._selectedItems.slice();
        }
        const result = this._getDiagramItems(items);
        let indices;
        if (!defined(undoable) || undoable) {
            indices = indicesOfItems(this.mainLayer, result.visuals);
            const unit = new ToFrontUnit(this, items, indices);
            this.undoRedoService.add(unit);
        }
        else {
            this.mainLayer.toFront(result.visuals);
            this._fixOrdering(result, true);
        }
    }
    /**
     * Sends to back the passed items.
     *
     * @param items DiagramElement, Array of Items.
     * @param undoable. By default the action is undoable.
     */
    toBack(items, undoable) {
        if (!items) {
            items = this._selectedItems.slice();
        }
        const result = this._getDiagramItems(items);
        let indices;
        if (!defined(undoable) || undoable) {
            indices = indicesOfItems(this.mainLayer, result.visuals);
            const unit = new ToBackUnit(this, items, indices);
            this.undoRedoService.add(unit);
        }
        else {
            this.mainLayer.toBack(result.visuals);
            this._fixOrdering(result, false);
        }
    }
    /**
     * Bring into view the passed item(s) or rectangle.
     *
     * @param items DiagramElement, Array of Items, Rect.
     * @param options. align - controls the position of the calculated rectangle relative to the viewport.
     * "Center middle" will position the items in the center. animate - controls if the pan should be animated.
     */
    bringIntoView(item, options) {
        const viewport = this.viewport();
        const aligner = new RectAlign(viewport);
        let rect;
        if (viewport.width === 0 || viewport.height === 0) {
            return;
        }
        options = deepExtend({ animate: false, align: 'center middle' }, options);
        if (options.align === 'none') {
            options.align = 'center middle';
        }
        if (item instanceof DiagramElement) {
            rect = item.bounds(TRANSFORMED);
        }
        else if (Array.isArray(item)) {
            rect = this.boundingBox(item);
        }
        else if (item instanceof Rect) {
            rect = item.clone();
        }
        const original = rect.clone();
        rect.zoom(this._zoom);
        if (rect.width > viewport.width || rect.height > viewport.height) {
            this._zoom = this._getValidZoom(Math.min(viewport.width / original.width, viewport.height / original.height));
            rect = original.clone().zoom(this._zoom);
        }
        this._zoomMainLayer();
        const current = rect.clone();
        aligner.align(rect, options.align);
        const newPan = rect.topLeft().minus(current.topLeft());
        this.pan(newPan.times(-1), options.animate);
    }
    /**
     * Aligns shapes in the specified direction.
     * @param direction The alignment direction ('left', 'right', 'top', 'bottom')
     */
    alignShapes(direction) {
        if (isUndefined(direction)) {
            direction = 'Left';
        }
        let val, item, i;
        const items = this.select();
        if (items.length === 0) {
            return;
        }
        switch (direction.toLowerCase()) {
            case 'left':
            case 'top':
                val = MAX_VALUE;
                break;
            case 'right':
            case 'bottom':
                val = MIN_VALUE;
                break;
            default:
                break;
        }
        for (i = 0; i < items.length; i++) {
            item = items[i];
            if (item instanceof Shape) {
                switch (direction.toLowerCase()) {
                    case 'left':
                        val = Math.min(val, item.options.x);
                        break;
                    case 'top':
                        val = Math.min(val, item.options.y);
                        break;
                    case 'right':
                        val = Math.max(val, item.options.x);
                        break;
                    case 'bottom':
                        val = Math.max(val, item.options.y);
                        break;
                    default:
                        break;
                }
            }
        }
        const undoStates = [];
        const shapes = [];
        for (i = 0; i < items.length; i++) {
            item = items[i];
            if (item instanceof Shape) {
                shapes.push(item);
                undoStates.push(item.bounds());
                switch (direction.toLowerCase()) {
                    case 'left':
                    case 'right':
                        item.position(new Point(val, item.options.y));
                        break;
                    case 'top':
                    case 'bottom':
                        item.position(new Point(item.options.x, val));
                        break;
                    default:
                        break;
                }
            }
        }
        const unit = new TransformUnit(shapes, undoStates);
        this.undoRedoService.add(unit, false);
    }
    /**
     * Gets or sets the zoom level of the diagram.
     * @param zoom The zoom level to set (1.0 = 100%). If not provided, returns the current zoom.
     * @param options Options including the zoom point and metadata
     * @returns The current zoom level when used as a getter
     */
    zoom(zoom, options) {
        if (zoom) {
            let staticPoint = options ? options.point : new Point(0, 0);
            // var meta = options ? options.meta : 0;
            zoom = this._zoom = this._getValidZoom(zoom);
            if (!isUndefined(staticPoint)) { // Viewpoint vector is constant
                staticPoint = new Point(Math.round(staticPoint.x), Math.round(staticPoint.y));
                const zoomedPoint = staticPoint.times(zoom);
                const viewportVector = this.modelToView(staticPoint);
                const raw = viewportVector.minus(zoomedPoint); // pan + zoomed point = viewpoint vector
                this._storePan(new Point(Math.round(raw.x), Math.round(raw.y)));
            }
            if (options) {
                options.zoom = zoom;
            }
            this._panTransform();
            if (this.canvas.surface.hideTooltip) {
                this.canvas.surface.hideTooltip();
            }
            this._updateAdorners();
        }
        return this._zoom;
    }
    /** @hidden */
    _getPan(pan) {
        const canvas = this.canvas;
        if (!canvas.translate) {
            pan = pan.plus(this._pan);
        }
        return pan;
    }
    /**
     * Gets or sets the pan offset of the diagram.
     * @param pan The pan offset as a Point. If not provided, returns the current pan.
     * @param animate Whether to animate the pan operation
     * @returns The current pan offset when used as a getter
     */
    pan(pan, animate) {
        if (pan instanceof Point) {
            const scroller = this.scroller;
            pan = this._getPan(pan);
            pan = pan.times(-1);
            if (animate) {
                scroller.animatedScrollTo(pan.x, pan.y, () => {
                    this._updateAdorners();
                });
            }
            else {
                scroller.scrollTo(pan.x, pan.y);
                this._updateAdorners();
            }
        }
        else {
            return this._pan.times(-1);
        }
    }
    /**
     * Gets the viewport rectangle of the diagram.
     * @returns A Rect representing the current viewport
     */
    viewport() {
        const element = this.element;
        const width = elementWidth(element);
        let height = elementHeight(element);
        if (this.toolBar) {
            height -= outerHeight(this.toolBar.element);
        }
        return new Rect(0, 0, width, height);
    }
    /**
     * Copies the currently selected items to the clipboard.
     */
    copy() {
        if (this.options.copy.enabled) {
            this._clipboard.length = 0;
            this._copyOffset = 1;
            for (let i = 0; i < this._selectedItems.length; i++) {
                const item = this._selectedItems[i];
                this._clipboard.push(item);
            }
        }
    }
    /**
     * Cuts the currently selected items to the clipboard and removes them from the diagram.
     */
    cut() {
        if (this.options.copy.enabled) {
            this._clipboard.length = 0;
            this._copyOffset = 0;
            for (let i = 0; i < this._selectedItems.length; i++) {
                const item = this._selectedItems[i];
                this._clipboard.push(item);
            }
            this.remove(this._clipboard, true);
        }
    }
    /**
     * Pastes items from the clipboard into the diagram.
     */
    paste() {
        if (this._clipboard.length > 0) {
            let item, copied, i;
            const mapping = {};
            const elements = splitDiagramElements(this._clipboard);
            const connections = elements.connections;
            const shapes = elements.shapes;
            const offset = {
                x: this._copyOffset * this.options.copy.offsetX,
                y: this._copyOffset * this.options.copy.offsetY
            };
            this.deselect();
            // first the shapes
            for (i = 0; i < shapes.length; i++) {
                item = shapes[i];
                copied = item.clone();
                mapping[item.id] = copied;
                copied.position(new Point(item.options.x + offset.x, item.options.y + offset.y));
                copied.diagram = this;
                copied = this._addShape(copied);
                if (copied) {
                    copied.select();
                }
            }
            // then the connections
            for (i = 0; i < connections.length; i++) {
                item = connections[i];
                copied = this._addConnection(item.clone());
                if (copied) {
                    this._updateCopiedConnection(copied, item, 'source', mapping, offset);
                    this._updateCopiedConnection(copied, item, 'target', mapping, offset);
                    copied.select(true);
                    copied.updateModel();
                }
            }
            this._syncChanges();
            this._copyOffset += 1;
        }
    }
    /** @hidden */
    _syncChanges() {
        if (this.options._syncChanges) {
            this.options._syncChanges();
        }
    }
    /** @hidden */
    _syncConnectionChanges() {
        if (this.options._syncConnectionChanges) {
            this.options._syncConnectionChanges();
        }
    }
    /** @hidden */
    _syncShapeChanges() {
        if (this.options._syncShapeChanges) {
            this.options._syncShapeChanges();
        }
    }
    /** @hidden */
    _updateCopiedConnection(connection, sourceConnection, connectorName, mapping, offset) {
        let onActivate, inactiveItem, targetShape;
        const target = sourceConnection[connectorName]();
        if (target instanceof Connector && mapping[target.shape.id]) {
            targetShape = mapping[target.shape.id];
            if (this.getShapeById(targetShape.id)) {
                connection[connectorName](targetShape.getConnector(target.options.name));
            }
            else {
                inactiveItem = this._inactiveShapeItems.getByUid(targetShape.dataItem.uid);
                if (inactiveItem) {
                    onActivate = (item) => {
                        targetShape = this._dataMap[item.id];
                        connection[connectorName](targetShape.getConnector(target.options.name));
                        connection.updateModel();
                    };
                    this._deferredConnectionUpdates.push(inactiveItem.onActivate(onActivate));
                }
            }
        }
        else {
            connection[connectorName](new Point(sourceConnection[connectorName + 'Point']().x + offset.x, sourceConnection[connectorName + 'Point']().y + offset.y));
        }
    }
    /**
     * Gets the bounding rectangle of the given items.
     *
     * @param items DiagramElement, Array of elements.
     * @param origin Boolean. Pass 'true' if you need to get the bounding box of the shapes without their rotation offset.
     * @returns {Rect} The bounding rectangle of the items.
     */
    boundingBox(items, origin) {
        let rect = Rect.empty(), temp;
        const di = isDefined(items) ? this._getDiagramItems(items) : { shapes: this.shapes };
        if (di.shapes.length > 0) {
            let item = di.shapes[0];
            rect = item.bounds(ROTATED);
            for (let i = 1; i < di.shapes.length; i++) {
                item = di.shapes[i];
                temp = item.bounds(ROTATED);
                if (origin === true) {
                    temp.x -= item._rotationOffset.x;
                    temp.y -= item._rotationOffset.y;
                }
                rect = rect.union(temp);
            }
        }
        return rect;
    }
    /** @hidden */
    _containerOffset() {
        const containerOffset = elementOffset(this.element);
        if (this.toolBar) {
            containerOffset.top += outerHeight(this.toolBar.element);
        }
        return containerOffset;
    }
    /**
     * Converts a point from document coordinates to view coordinates.
     * @param point The point in document coordinates
     * @returns The point in view coordinates
     */
    documentToView(point) {
        const containerOffset = this._containerOffset();
        return new Point(point.x - containerOffset.left, point.y - containerOffset.top);
    }
    /**
     * Converts a point from view coordinates to document coordinates.
     * @param point The point in view coordinates
     * @returns The point in document coordinates
     */
    viewToDocument(point) {
        const containerOffset = this._containerOffset();
        return new Point(point.x + containerOffset.left, point.y + containerOffset.top);
    }
    /**
     * Converts a point from view coordinates to model coordinates.
     * @param point The point in view coordinates
     * @returns The point in model coordinates
     */
    viewToModel(point) {
        return this._transformWithMatrix(point, this._matrixInvert);
    }
    /**
     * Converts a point from model coordinates to view coordinates.
     * @param point The point in model coordinates
     * @returns The point in view coordinates
     */
    modelToView(point) {
        return this._transformWithMatrix(point, this._matrix);
    }
    /**
     * Converts a point from model coordinates to layer coordinates.
     * @param point The point in model coordinates
     * @returns The point in layer coordinates
     */
    modelToLayer(point) {
        return this._transformWithMatrix(point, this._layerMatrix);
    }
    /**
     * Converts a point from layer coordinates to model coordinates.
     * @param point The point in layer coordinates
     * @returns The point in model coordinates
     */
    layerToModel(point) {
        return this._transformWithMatrix(point, this._layerMatrixInvert);
    }
    /**
     * Converts a point from document coordinates to model coordinates.
     * @param point The point in document coordinates
     * @returns The point in model coordinates
     */
    documentToModel(point) {
        const viewPoint = this.documentToView(point);
        if (!this.canvas.translate) {
            viewPoint.x = viewPoint.x + this.scroller.scrollLeft;
            viewPoint.y = viewPoint.y + this.scroller.scrollTop;
        }
        return this.viewToModel(viewPoint);
    }
    /**
     * Converts a point from model coordinates to document coordinates.
     * @param point The point in model coordinates
     * @returns The point in document coordinates
     */
    modelToDocument(point) {
        return this.viewToDocument(this.modelToView(point));
    }
    /** @hidden */
    _transformWithMatrix(point, matrix) {
        let result = point;
        if (point instanceof Point) {
            if (matrix) {
                result = matrix.apply(point);
            }
        }
        else {
            const tl = this._transformWithMatrix(point.topLeft(), matrix), br = this._transformWithMatrix(point.bottomRight(), matrix);
            result = Rect.fromPoints(tl, br);
        }
        return result;
    }
    /**
     * Performs a diagram layout of the given type.
     *
     * @param layoutType The layout algorithm to be applied (TreeLayout, LayeredLayout, SpringLayout).
     * @param options Layout-specific options.
     */
    layout(options) {
        this._layouting = true;
        // TODO: raise layout event?
        let type;
        if (isUndefined(options)) {
            options = this.options.layout;
        }
        if (isUndefined(options) || isUndefined(options.type)) {
            type = 'Tree';
        }
        else {
            type = options.type;
        }
        let l;
        switch (type.toLowerCase()) {
            case 'tree':
                l = new TreeLayout(this);
                break;
            case 'layered':
                l = new LayeredLayout(this);
                break;
            case 'forcedirected':
            case 'force':
            case 'spring':
            case 'springembedder':
                l = new SpringLayout(this);
                break;
            default:
                throw new Error('Layout algorithm \'' + type + '\' is not supported.');
        }
        const initialState = new LayoutState(this);
        const finalState = l.layout(options);
        if (finalState) {
            const unit = new LayoutUndoUnit(initialState, finalState, options ? options.animate : null);
            this.undoRedoService.add(unit);
        }
        this._layouting = false;
        this._redrawConnections();
    }
    /**
     * Gets a shape by its unique identifier.
     * @param {string} id The unique identifier of the shape
     * @returns {Shape} The shape with the specified ID, or undefined if not found
     */
    getShapeById(id) {
        let found;
        found = first(this.shapes, function (s) {
            return s.visual.id === id;
        });
        if (found) {
            return found;
        }
        found = first(this.connections, function (c) {
            return c.visual.id === id;
        });
        return found;
    }
    /**
     * Gets a shape by its model ID.
     * @param id The model ID of the shape
     * @returns The shape with the specified model ID, or undefined if not found
     */
    getShapeByModelId(id) {
        let shapeResult;
        if (this._isEditable) {
            shapeResult = this._dataMap[id];
        }
        else {
            shapeResult = first(this.shapes, function (shape) {
                return (shape.dataItem || {}).id === id;
            });
        }
        return shapeResult;
    }
    /**
     * Gets a shape by its model UID.
     * @param uid The model UID of the shape
     * @returns The shape with the specified model UID, or undefined if not found
     */
    getShapeByModelUid(uid) {
        let shapeResult;
        if (this._isEditable) {
            shapeResult = first(this.shapes, function (shape) {
                return (shape.dataItem || {}).uid === uid;
            });
        }
        else {
            shapeResult = this._dataMap[uid];
        }
        return shapeResult;
    }
    /** @hidden */
    _extendLayoutOptions(options) {
        if (options.layout) {
            options.layout = deepExtend({}, LayoutDefaultOptions, options.layout);
        }
    }
    /** @hidden */
    _selectionChanged(selected, deselected) {
        if (selected.length || deselected.length) {
            this.trigger(SELECT, { selected: selected, deselected: deselected });
        }
    }
    /** @hidden */
    _getValidZoom(zoom) {
        return Math.min(Math.max(zoom, this.options.zoomMin), this.options.zoomMax);
    }
    /** @hidden */
    _panTransform(pos) {
        const pan = pos || this._pan;
        if (this.canvas.translate) {
            this.scroller.scrollTo(pan.x, pan.y);
            this._zoomMainLayer();
        }
        else {
            this._storePan(pan);
            this._transformMainLayer();
        }
    }
    /** @hidden */
    _finishPan() {
        this.trigger(PAN, { total: this._pan, delta: Number.NaN });
    }
    /** @hidden */
    _storePan(pan) {
        this._pan = pan;
        this._storeViewMatrix();
    }
    /** @hidden */
    _zoomMainLayer() {
        const zoom = this._zoom;
        const transform = new CompositeTransform(0, 0, zoom, zoom);
        transform.render(this.mainLayer);
        this._storeLayerMatrix(transform);
        this._storeViewMatrix();
    }
    /** @hidden */
    _transformMainLayer() {
        const pan = this._pan, zoom = this._zoom;
        const transform = new CompositeTransform(pan.x, pan.y, zoom, zoom);
        transform.render(this.mainLayer);
        this._storeLayerMatrix(transform);
        this._storeViewMatrix();
    }
    /** @hidden */
    _storeLayerMatrix(canvasTransform) {
        this._layerMatrix = canvasTransform.toMatrix();
        this._layerMatrixInvert = canvasTransform.invert().toMatrix();
    }
    /** @hidden */
    _storeViewMatrix() {
        const pan = this._pan, zoom = this._zoom;
        const transform = new CompositeTransform(pan.x, pan.y, zoom, zoom);
        this._matrix = transform.toMatrix();
        this._matrixInvert = transform.invert().toMatrix();
    }
    /** @hidden */
    _toIndex(items, indices) {
        const result = this._getDiagramItems(items);
        this.mainLayer.toIndex(result.visuals, indices);
        this._fixOrdering(result, false);
    }
    /** @hidden */
    _fixOrdering(result, toFront) {
        const shapePos = toFront ? this.shapes.length - 1 : 0, conPos = toFront ? this.connections.length - 1 : 0;
        let i, item;
        for (i = 0; i < result.shapes.length; i++) {
            item = result.shapes[i];
            remove(this.shapes, item);
            insert(this.shapes, item, shapePos);
        }
        for (i = 0; i < result.cons.length; i++) {
            item = result.cons[i];
            remove(this.connections, item);
            insert(this.connections, item, conPos);
        }
    }
    /** @hidden */
    _getDiagramItems(items) {
        let i, args = items;
        const result = {};
        result.visuals = [];
        result.shapes = [];
        result.cons = [];
        if (!items) {
            args = this._selectedItems.slice();
        }
        else if (!Array.isArray(items)) {
            args = [items];
        }
        for (i = 0; i < args.length; i++) {
            const item = args[i];
            if (item instanceof Shape) {
                result.shapes.push(item);
                result.visuals.push(item.visual);
            }
            else if (item instanceof Connection) {
                result.cons.push(item);
                result.visuals.push(item.visual);
            }
        }
        return result;
    }
    /** @hidden */
    _addDataItemByUid(dataItem) {
        if (!defined(dataItem)) {
            return;
        }
        let shape = this._dataMap[dataItem.uid];
        if (shape) {
            return shape;
        }
        const options = deepExtend({}, this.options.shapeDefaults);
        options.dataItem = dataItem;
        shape = new Shape(options, this);
        this.addShape(shape);
        this._dataMap[dataItem.uid] = shape;
        return shape;
    }
    /** @hidden */
    _addItem(item) {
        if (item instanceof Shape) {
            this.addShape(item);
        }
        else if (item instanceof Connection) {
            this.addConnection(item);
        }
    }
    /** @hidden */
    _toolBarClick(e) {
        this.trigger('toolBarClick', e);
        this._destroyToolBar();
    }
    /** @hidden */
    _normalizePointZoom(point) {
        return point.times(1 / this.zoom());
    }
    /** @hidden */
    _initialize() {
        this.shapes.length = 0;
        this.connections.length = 0;
        this._selectedItems.length = 0;
        Object.keys(this._dataMap).forEach((key) => {
            delete this._dataMap[key];
        });
        Object.keys(this._connectionsDataMap).forEach((key) => {
            delete this._connectionsDataMap[key];
        });
        this._deferredConnectionUpdates.length = 0;
        this.undoRedoService = new UndoRedoService({
            undone: this._syncChanges.bind(this),
            redone: this._syncChanges.bind(this)
        });
        this.undoRedoService.bind('undo', (args) => {
            this.trigger('undo', args);
        });
        this.undoRedoService.bind('redo', (args) => {
            this.trigger('redo', args);
        });
        this.id = randomId();
    }
    /** @hidden */
    _redrawConnections() {
        const connections = this.connections;
        for (let idx = 0; idx < connections.length; idx++) {
            connections[idx].refresh();
        }
    }
    /** @hidden */
    _adorn(adorner, isActive) {
        if (isActive !== undefined && adorner) {
            if (isActive) {
                this._adorners.push(adorner);
                this.adornerLayer.append(adorner.visual);
            }
            else {
                remove(this._adorners, adorner);
                this.adornerLayer.remove(adorner.visual);
            }
        }
    }
    /** @hidden */
    _showConnectors(shape, value) {
        if (value) {
            this._connectorsAdorner.show(shape);
        }
        else {
            this._connectorsAdorner.destroy();
        }
    }
    /** @hidden */
    _updateAdorners() {
        const adorners = this._adorners;
        for (let i = 0; i < adorners.length; i++) {
            const adorner = adorners[i];
            if (adorner.refreshBounds) {
                adorner.refreshBounds();
            }
            adorner.refresh();
        }
    }
    /** @hidden */
    _refresh() {
        for (let i = 0; i < this.connections.length; i++) {
            this.connections[i].refresh();
        }
    }
    /** @hidden */
    _removeItem(item, undoable, removedConnections) {
        item.select(false);
        if (item instanceof Shape) {
            this._removeShapeDataItem(item);
            this._removeShape(item, undoable, removedConnections);
        }
        else if (item instanceof Connection) {
            this._removeConnectionDataItem(item);
            this._removeConnection(item, undoable);
        }
        this.mainLayer.remove(item.visual);
    }
    /** @hidden */
    _removeConnectionDataItem(item) {
        if (this._isEditable) {
            this.options._removeConnectionDataItem(item.dataItem);
            delete this._connectionsDataMap[item.dataItem.uid];
        }
    }
    /** @hidden */
    _removeShapeDataItem(item) {
        if (this._isEditable) {
            this.options._removeShapeDataItem(item.dataItem);
            delete this._dataMap[item.dataItem.id];
        }
    }
    /** @hidden */
    _removeShape(shape, undoable, removedConnections) {
        let i, connection, connector;
        const sources = [], targets = [];
        this.toolService._removeHover();
        if (undoable) {
            this.undoRedoService.addCompositeItem(new DeleteShapeUnit(shape));
        }
        remove(this.shapes, shape);
        this._shapesQuadTree.remove(shape);
        for (i = 0; i < shape.connectors.length; i++) {
            connector = shape.connectors[i];
            for (let j = 0; j < connector.connections.length; j++) {
                connection = connector.connections[j];
                if (!removedConnections || !contains(removedConnections, connection)) {
                    if (connection.sourceConnector === connector) {
                        sources.push(connection);
                    }
                    else if (connection.targetConnector === connector) {
                        targets.push(connection);
                    }
                }
            }
        }
        for (i = 0; i < sources.length; i++) {
            sources[i].source(null, undoable);
            sources[i].updateModel();
        }
        for (i = 0; i < targets.length; i++) {
            targets[i].target(null, undoable);
            targets[i].updateModel();
        }
    }
    /** @hidden */
    _removeConnection(connection, undoable) {
        if (connection.sourceConnector) {
            remove(connection.sourceConnector.connections, connection);
        }
        if (connection.targetConnector) {
            remove(connection.targetConnector.connections, connection);
        }
        if (undoable) {
            this.undoRedoService.addCompositeItem(new DeleteConnectionUnit(connection));
        }
        remove(this.connections, connection);
    }
    /** @hidden */
    _removeShapeConnections(shape) {
        const connections = shape.connections();
        let idx;
        if (connections) {
            for (idx = 0; idx < connections.length; idx++) {
                this._removeItem(connections[idx], false);
            }
        }
    }
    /** @hidden */
    _destroyToolBar() {
        this.options.destroyToolBar();
    }
    /** @hidden */
    _destroyGlobalToolBar() {
        if (this.toolBar) {
            this.toolBar = null;
        }
    }
    /** @hidden */
    _mobileOS() {
        return mobileOS;
    }
    /**
     * Exports the diagram's DOM visual representation for rendering or export purposes.
     * Creates a clipped group containing the canvas content with proper transformations.
     * @returns A drawing Group element containing the exported DOM visual
     */
    exportDOMVisual() {
        const viewBox = this.canvas._viewBox;
        const scrollOffset = geom.transform()
            .translate(-viewBox.x, -viewBox.y);
        const viewRect = new geom.Rect([0, 0], [viewBox.width, viewBox.height]);
        const clipPath = draw.Path.fromRect(viewRect);
        const wrap = new draw.Group({ transform: scrollOffset });
        const clipWrap = new draw.Group({ clip: clipPath });
        const root = this.canvas.drawingElement.children[0];
        clipWrap.append(wrap);
        // Don't reparent the root
        wrap.children.push(root);
        return clipWrap;
    }
    /**
     * Exports the diagram's visual representation with proper scaling based on zoom level.
     * Creates a scaled group containing the main layer content.
     * @returns A drawing Group element containing the exported visual with inverse zoom scaling
     */
    exportVisual() {
        const scaleX = 1 / this._zoom;
        const scale = geom.transform().scale(scaleX, scaleX);
        const wrap = new draw.Group({
            transform: scale
        });
        const root = this.mainLayer.drawingElement;
        wrap.children.push(root);
        return wrap;
    }
    /**
     * Updates the connection's underlying data model.
     * Calls the configured updateConnectionModel function if available.
     * @param connection The connection whose model should be updated
     * @param syncChanges Whether to synchronize changes immediately
     * @returns The result of the update operation, if any
     */
    updateConnectionModel(connection, syncChanges) {
        if (this.options.updateConnectionModel) {
            return this.options.updateConnectionModel(connection, syncChanges);
        }
    }
    /**
     * Updates the shape's underlying data model.
     * Calls the configured updateShapeModel function if available.
     * @param shape The shape whose model should be updated
     * @param syncChanges Whether to synchronize changes immediately
     * @returns The result of the update operation, if any
     */
    updateShapeModel(shape, syncChanges) {
        if (this.options.updateShapeModel) {
            return this.options.updateShapeModel(shape, syncChanges);
        }
    }
}
